import { useRef, useState } from 'react';
import { MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import {
  CHART_TYPES, DATE_RANGE_LABELS, DATE_RANGES, GROUP_BY_LABELS, METRIC_LABELS,
  type ChartLine, type ChartType, type DateRange, type GroupBy, type Metric, type Widget, type WidgetModule,
} from '@unitly/contracts';
import { useChart, useDeleteWidget, useWidgetMutation, useWidgets } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { PageLoader } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';

const PALETTE = ['indigo', 'blue', 'teal', 'green', 'orange', 'pink', 'purple', 'red', 'amber', 'lime'] as const;
const CHART_TYPE_LABELS: Record<ChartType, string> = { bar: 'Barre', line: 'Linea', donut: 'Ciambella' };

function fmt(n: number, decimals: number) {
  return n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Every label that appears in any line, in first-seen order (lines share the same groupBy, so they mostly overlap). */
function sharedCategories(lines: ChartLine[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines) for (const p of line.points) if (!seen.has(p.label)) { seen.add(p.label); out.push(p.label); }
  return out.sort();
}

/**
 * A workspace's customizable "Report" tab: a grid of saved chart widgets (bar/line/donut plotting one
 * or more metrics, picked with checkboxes, over any grouping the module exposes), plus a form to add
 * or remove them. Available metrics/groupings are declared by the caller (Finance, CRM, …).
 */
export function WidgetBoard({
  workspaceId, module, metrics, groupBys, isMoney = false, canEdit, sectionId,
}: {
  workspaceId: string; module: WidgetModule; metrics: Metric[]; groupBys: GroupBy[]; isMoney?: boolean; canEdit: boolean; sectionId?: string;
}) {
  const { orgId } = useOrgCtx();
  const widgets = useWidgets(orgId, workspaceId, module, sectionId);
  const del = useDeleteWidget(orgId, workspaceId, module, sectionId);
  const [editing, setEditing] = useState<Widget | 'new' | null>(null);
  const newSeq = useRef(0);
  const openNew = () => { newSeq.current += 1; setEditing('new'); };

  return (
    <>
      <div className="toolbar">
        {canEdit && <button type="button" className="btn btn-primary btn-sm" onClick={openNew}><Plus size={16} /> Grafico</button>}
      </div>
      {widgets.isLoading && <PageLoader />}
      {widgets.data?.length === 0 && (
        <div className="card card-pad" style={{ textAlign: 'center', color: 'var(--label-2)' }}>
          Nessun grafico ancora. Aggiungine uno per visualizzare i dati come preferisci.
        </div>
      )}
      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {widgets.data?.map((w) => (
          <ChartCard key={w.id} workspaceId={workspaceId} module={module} widget={w} isMoney={isMoney}
            onEdit={canEdit ? () => setEditing(w) : undefined}
            onDelete={canEdit ? () => del.mutate(w.id) : undefined} />
        ))}
      </div>
      <WidgetFormSheet
        key={editing === 'new' ? `new-${newSeq.current}` : (editing?.id ?? 'closed')}
        open={editing !== null} onClose={() => setEditing(null)} workspaceId={workspaceId} module={module} sectionId={sectionId}
        widget={editing === 'new' ? null : editing} metrics={metrics} groupBys={groupBys}
      />
    </>
  );
}

function ChartCard({ workspaceId, module, widget, isMoney, onEdit, onDelete }: {
  workspaceId: string; module: WidgetModule; widget: Widget; isMoney: boolean; onEdit?: () => void; onDelete?: () => void;
}) {
  const { orgId } = useOrgCtx();
  const chart = useChart(orgId, workspaceId, module, widget);
  const [menuOpen, setMenuOpen] = useState(false);
  const lines = chart.data?.lines ?? [];
  const categories = sharedCategories(lines);
  const max = Math.max(1, ...lines.flatMap((l) => l.points.map((p) => Math.abs(p.value))));
  const decimals = isMoney ? 2 : 0;
  const suffix = isMoney ? ' €' : '';
  const hasData = lines.some((l) => l.points.length > 0);
  const metricLabel = (m: string) => METRIC_LABELS[m as Metric] ?? m;

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 220 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span className="grow">
          <strong style={{ fontSize: 15 }}>{widget.name}</strong>
          <div className="hint" style={{ padding: 0 }}>
            {widget.metrics.map(metricLabel).join(' · ')} per {GROUP_BY_LABELS[widget.groupBy as GroupBy] ?? widget.groupBy} · {DATE_RANGE_LABELS[widget.dateRange]}
          </div>
        </span>
        {(onEdit || onDelete) && (
          <div style={{ position: 'relative' }}>
            <button type="button" className="icon-btn muted" style={{ width: 28, height: 28 }} aria-label="Opzioni grafico" onClick={() => setMenuOpen((v) => !v)}>
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div className="card" style={{ position: 'absolute', top: 30, right: 0, zIndex: 5, minWidth: 140, boxShadow: 'var(--shadow-2)' }}>
                <div className="list">
                  {onEdit && <button type="button" className="list-row" onClick={() => { setMenuOpen(false); onEdit(); }}>Modifica</button>}
                  {onDelete && <button type="button" className="list-row danger" onClick={() => { setMenuOpen(false); onDelete(); }}><Trash2 size={15} /> Elimina</button>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {widget.metrics.length > 1 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {widget.metrics.map((m, i) => (
            <span key={m} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: `var(--ws-${PALETTE[i % PALETTE.length]})` }} />
              {metricLabel(m)}
            </span>
          ))}
        </div>
      )}

      {chart.isLoading && <PageLoader />}
      {chart.data && !hasData && <p className="hint" style={{ flex: 1 }}>Nessun dato per questo periodo.</p>}
      {chart.data && hasData && widget.chartType === 'bar' && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 10, minHeight: 120 }}>
          {categories.map((cat) => (
            <div key={cat} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: '100%' }}>
                {lines.map((line, i) => {
                  const v = line.points.find((p) => p.label === cat)?.value ?? 0;
                  return (
                    <div key={line.metric} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                      {lines.length === 1 && <span style={{ fontSize: 11, color: 'var(--label-2)' }}>{fmt(v, v % 1 === 0 ? 0 : decimals)}</span>}
                      <div title={`${metricLabel(line.metric)}: ${fmt(v, decimals)}${suffix}`} style={{
                        width: lines.length > 1 ? 14 : 28, borderRadius: 4, background: `var(--ws-${PALETTE[i % PALETTE.length]})`,
                        height: `${Math.max(4, (Math.abs(v) / max) * 100)}%`,
                      }} />
                    </div>
                  );
                })}
              </div>
              <span style={{ fontSize: 10.5, color: 'var(--label-3)', textAlign: 'center', lineHeight: 1.2 }}>{cat}</span>
            </div>
          ))}
        </div>
      )}
      {chart.data && hasData && widget.chartType === 'line' && <LineChart lines={lines} categories={categories} max={max} />}
      {chart.data && hasData && widget.chartType === 'donut' && (
        <>
          {widget.metrics.length > 1 && <p className="hint" style={{ padding: 0 }}>La ciambella mostra solo “{metricLabel(widget.metrics[0]!)}”.</p>}
          <DonutChart points={lines[0]!.points} suffix={suffix} decimals={decimals} />
        </>
      )}
      {chart.data && hasData && (
        <p className="hint" style={{ padding: 0, marginTop: 'auto' }}>
          {lines.map((l) => `${lines.length > 1 ? `${metricLabel(l.metric)}: ` : 'Totale: '}${fmt(l.total, decimals)}${suffix}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

function LineChart({ lines, categories, max }: { lines: ChartLine[]; categories: string[]; max: number }) {
  const w = 100, h = 60, pad = 4;
  const min = Math.min(0, ...lines.flatMap((l) => l.points.map((p) => p.value)));
  const range = max - min || 1;
  const xFor = (i: number) => (categories.length > 1 ? (i / (categories.length - 1)) * (w - pad * 2) + pad : w / 2);
  const yFor = (v: number) => h - pad - ((v - min) / range) * (h - pad * 2);

  return (
    <div style={{ flex: 1, minHeight: 120 }}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100" preserveAspectRatio="none" role="img" aria-label="Grafico a linea">
        {lines.map((line, li) => {
          const coords = categories.map((cat, i) => [xFor(i), yFor(line.points.find((p) => p.label === cat)?.value ?? 0)] as const);
          const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
          const color = `var(--ws-${PALETTE[li % PALETTE.length]})`;
          return (
            <g key={line.metric}>
              <path d={path} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
              {coords.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={1.6} fill={color} />)}
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--label-3)' }}>
        <span>{categories[0]}</span>
        {categories.length > 1 && <span>{categories[categories.length - 1]}</span>}
      </div>
    </div>
  );
}

function DonutChart({ points, suffix, decimals }: { points: { label: string; value: number }[]; suffix: string; decimals: number }) {
  const total = points.reduce((s, p) => s + Math.abs(p.value), 0) || 1;
  let acc = 0;
  const stops = points.map((p, i) => {
    const start = (acc / total) * 100;
    acc += Math.abs(p.value);
    const end = (acc / total) * 100;
    return `var(--ws-${PALETTE[i % PALETTE.length]}) ${start}% ${end}%`;
  });
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flex: 1 }}>
      <div style={{ width: 96, height: 96, borderRadius: '50%', flex: 'none', background: `conic-gradient(${stops.join(', ')})`, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 14, borderRadius: '50%', background: 'var(--surface)' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {points.map((p, i) => (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: `var(--ws-${PALETTE[i % PALETTE.length]})`, flex: 'none' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</span>
            <span style={{ color: 'var(--label-2)', marginLeft: 'auto', paddingLeft: 8 }}>{fmt(p.value, decimals)}{suffix}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WidgetFormSheet({ open, onClose, workspaceId, module, widget, metrics, groupBys, sectionId }: {
  open: boolean; onClose: () => void; workspaceId: string; module: WidgetModule; widget: Widget | null; metrics: Metric[]; groupBys: GroupBy[]; sectionId?: string;
}) {
  const { orgId } = useOrgCtx();
  const mutate = useWidgetMutation(orgId, workspaceId, module, sectionId);
  const toast = useToast();
  const [form, setForm] = useState(() => ({
    name: widget?.name ?? '', chartType: widget?.chartType ?? 'bar' as ChartType, metrics: widget?.metrics ?? [metrics[0]!],
    groupBy: (widget?.groupBy ?? groupBys[0]) as string, dateRange: (widget?.dateRange ?? 'last_6_months') as DateRange,
  }));
  const [key, setKey] = useState(widget?.id ?? 'new');
  if ((widget?.id ?? 'new') !== key) {
    setForm({ name: widget?.name ?? '', chartType: widget?.chartType ?? 'bar', metrics: widget?.metrics ?? [metrics[0]!], groupBy: (widget?.groupBy ?? groupBys[0]) as string, dateRange: widget?.dateRange ?? 'last_6_months' });
    setKey(widget?.id ?? 'new');
  }
  const toggleMetric = (m: Metric) => setForm((f) => ({
    ...f,
    metrics: f.metrics.includes(m) ? (f.metrics.length > 1 ? f.metrics.filter((x) => x !== m) : f.metrics) : [...f.metrics, m],
  }));

  return (
    <Sheet open={open} onClose={onClose} variant="center" title={widget ? 'Modifica grafico' : 'Nuovo grafico'} labelledBy="widget-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!form.name.trim() || mutate.isPending}
            onClick={() => mutate.mutate({ id: widget?.id, patch: form }, { onSuccess: onClose, onError: (e) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito') })}>
            Salva
          </button>
        </div>
      }>
      <label className="field"><span>Nome</span><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Entrate per categoria" /></label>
      <div className="field">
        <span>Tipo di grafico</span>
        <select className="input" value={form.chartType} onChange={(e) => {
          const chartType = e.target.value as ChartType;
          setForm({ ...form, chartType, metrics: chartType === 'donut' ? [form.metrics[0]!] : form.metrics });
        }}>
          {CHART_TYPES.map((t) => <option key={t} value={t}>{CHART_TYPE_LABELS[t]}</option>)}
        </select>
      </div>
      <div className="field">
        <span>Cosa vuoi vedere {form.chartType !== 'donut' && '(scegline anche più di una)'}</span>
        <div className="card" style={{ padding: '4px 4px' }}>
          {metrics.map((m) => (
            <label key={m} className="section-row" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox" checked={form.metrics.includes(m)}
                onChange={() => (form.chartType === 'donut' ? setForm({ ...form, metrics: [m] }) : toggleMetric(m))}
              />
              <span className="grow">{METRIC_LABELS[m]}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="field">
        <span>Raggruppa per</span>
        <select className="input" value={form.groupBy} onChange={(e) => setForm({ ...form, groupBy: e.target.value })}>
          {groupBys.map((g) => <option key={g} value={g}>{GROUP_BY_LABELS[g]}</option>)}
        </select>
      </div>
      <div className="field">
        <span>Periodo</span>
        <select className="input" value={form.dateRange} onChange={(e) => setForm({ ...form, dateRange: e.target.value as DateRange })}>
          {DATE_RANGES.map((r) => <option key={r} value={r}>{DATE_RANGE_LABELS[r]}</option>)}
        </select>
      </div>
    </Sheet>
  );
}
